import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Switch
} from '@mui/material';
import { GlobalContext } from '../../utils/ContextProviders/GlobalContextProvider';
import { getCommunities } from '../../Redux/util/Apis/communities';
import { createLead } from '../../Redux/util/Apis/leads';

const STEPS = ['Lead Info', 'Property', 'Preferences', 'Settings'];

const PAYLOAD_SOURCES = [
  'portal',
  'zuma-chatbot',
  'webhook',
  'api',
  'leadPoller',
  'ivr',
  'emailParser',
  'websiteBot'
];

const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  clientId: '',
  communityId: '',
  source: '',
  bedrooms: '',
  bathrooms: '',
  moveInDate: '',
  leaseTerm: '',
  budgetMin: '',
  budgetMax: '',
  furnished: '',
  message: '',
  syncToCRM: true,
  testMode: false,
  payloadSource: 'portal',
  enableAFM: false
};

const CreateLeadModal = ({ open, onClose, onSuccess }) => {
  const { userData } = useContext(GlobalContext);
  const clients = userData?.clients || [];

  const [form, setForm] = useState(INITIAL_FORM);
  const [communities, setCommunities] = useState([]);
  const [loadingCommunities, setLoadingCommunities] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setForm(INITIAL_FORM);
      setCommunities([]);
      setError('');
      setActiveStep(0);
    }
  }, [open]);

  // Fetch communities when client changes
  useEffect(() => {
    if (!form.clientId) {
      setCommunities([]);
      return;
    }
    setLoadingCommunities(true);
    getCommunities(form.clientId)
      .then(res => {
        if (res?.error) {
          setCommunities([]);
          return;
        }
        const data = Array.isArray(res) ? res : res?.data || [];
        setCommunities(data);
      })
      .catch(() => setCommunities([]))
      .finally(() => setLoadingCommunities(false));
  }, [form.clientId]);

  // Reset community when client changes
  const handleClientChange = clientId => {
    setForm(prev => ({ ...prev, clientId, communityId: '' }));
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = step => {
    switch (step) {
      case 0:
        if (!form.name.trim()) return 'Name is required';
        return null;
      case 1:
        if (!form.clientId) return 'Client is required';
        if (!form.communityId) return 'Community is required';
        if (!form.source.trim()) return 'Source is required';
        return null;
      case 2:
        if (!form.moveInDate) return 'Move-in date is required';
        return null;
      case 3:
        if (!form.payloadSource) return 'Payload source is required';
        return null;
      default:
        return null;
    }
  };

  const handleNext = () => {
    const validationError = validateStep(activeStep);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    const validationError = validateStep(activeStep);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setSubmitting(true);

    const payload = {
      name: form.name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      tenantId: form.clientId,
      communityId: form.communityId,
      source: form.source.trim(),
      payloadSource: form.payloadSource,
      bedrooms: form.bedrooms !== '' ? Number(form.bedrooms) : null,
      bathrooms: form.bathrooms !== '' ? Number(form.bathrooms) : null,
      moveInDate: form.moveInDate,
      leaseTerm: form.leaseTerm.trim() || null,
      budgetMin: form.budgetMin.trim() || null,
      budgetMax: form.budgetMax.trim() || null,
      furnished: form.furnished || null,
      message: form.message.trim() || null,
      createdAt: new Date().toISOString(),
      syncToCRM: form.syncToCRM,
      testMode: form.testMode,
      enableAFM: form.enableAFM
    };

    const res = await createLead(payload);

    if (res?.error) {
      setError(res.message || 'Failed to create lead');
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    onSuccess();
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <>
            <Typography variant="subtitle2" color="text.secondary">
              Contact Information
            </Typography>
            <TextField
              label="Name"
              value={form.name}
              onChange={e => handleChange('name', e.target.value)}
              size="small"
              required
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={e => handleChange('email', e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Phone"
              value={form.phone}
              onChange={e => handleChange('phone', e.target.value)}
              size="small"
              fullWidth
            />
          </>
        );

      case 1:
        return (
          <>
            <Typography variant="subtitle2" color="text.secondary">
              Property Details
            </Typography>
            <FormControl size="small" fullWidth required>
              <InputLabel>Client</InputLabel>
              <Select
                value={form.clientId}
                onChange={e => handleClientChange(e.target.value)}
                label="Client"
              >
                {clients.map(c => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              fullWidth
              required
              disabled={!form.clientId || loadingCommunities}
            >
              <InputLabel>Community</InputLabel>
              <Select
                value={form.communityId}
                onChange={e => handleChange('communityId', e.target.value)}
                label="Community"
              >
                {communities.map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Source"
              value={form.source}
              onChange={e => handleChange('source', e.target.value)}
              size="small"
              required
              fullWidth
            />
          </>
        );

      case 2:
        return (
          <>
            <Typography variant="subtitle2" color="text.secondary">
              Lead Preferences
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Bedrooms</InputLabel>
                <Select
                  value={form.bedrooms}
                  onChange={e => handleChange('bedrooms', e.target.value)}
                  label="Bedrooms"
                >
                  <MenuItem value="">None</MenuItem>
                  {[0, 1, 2, 3, 4, 5].map(n => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Bathrooms</InputLabel>
                <Select
                  value={form.bathrooms}
                  onChange={e => handleChange('bathrooms', e.target.value)}
                  label="Bathrooms"
                >
                  <MenuItem value="">None</MenuItem>
                  {[0, 1, 2, 3, 4, 5].map(n => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Move-in Date"
              type="date"
              value={form.moveInDate}
              onChange={e => handleChange('moveInDate', e.target.value)}
              size="small"
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Lease Term"
                value={form.leaseTerm}
                onChange={e => handleChange('leaseTerm', e.target.value)}
                size="small"
                fullWidth
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Furnished</InputLabel>
                <Select
                  value={form.furnished}
                  onChange={e => handleChange('furnished', e.target.value)}
                  label="Furnished"
                >
                  <MenuItem value="">N/A</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Budget Min"
                value={form.budgetMin}
                onChange={e => handleChange('budgetMin', e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Budget Max"
                value={form.budgetMax}
                onChange={e => handleChange('budgetMax', e.target.value)}
                size="small"
                fullWidth
              />
            </Box>
          </>
        );

      case 3:
        return (
          <>
            <Typography variant="subtitle2" color="text.secondary">
              Submission Settings
            </Typography>
            <FormControl size="small" fullWidth required>
              <InputLabel>Payload Source</InputLabel>
              <Select
                value={form.payloadSource}
                onChange={e => handleChange('payloadSource', e.target.value)}
                label="Payload Source"
              >
                {PAYLOAD_SOURCES.map(src => (
                  <MenuItem key={src} value={src}>
                    {src}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.syncToCRM}
                    onChange={e => handleChange('syncToCRM', e.target.checked)}
                  />
                }
                label="Sync to CRM"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.testMode}
                    onChange={e => handleChange('testMode', e.target.checked)}
                  />
                }
                label="Test Mode"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.enableAFM}
                    onChange={e => handleChange('enableAFM', e.target.checked)}
                  />
                }
                label="Send AFM"
              />
            </Box>
            <TextField
              label="Message"
              value={form.message}
              onChange={e => handleChange('message', e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={3}
            />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>New Lead</DialogTitle>
      <Box sx={{ px: 3, pt: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {renderStepContent()}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {activeStep > 0 && <Button onClick={handleBack}>Back</Button>}
        {activeStep < STEPS.length - 1 ? (
          <Button variant="contained" onClick={handleNext}>
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            Create Lead
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateLeadModal;
